/* Arctic Elegance — global behaviour: dialogs, header, announcement, wishlist, quantity, toast */
(function () {
  'use strict';

  var theme = window.theme || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Toast ---------- */
  var toastTimer;
  theme.toast = function (message) {
    var el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3200);
  };

  /* ---------- Money ---------- */
  theme.formatMoney = function (cents) {
    try {
      return new Intl.NumberFormat(theme.locale || 'en-US', { style: 'currency', currency: theme.currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* ---------- Dialogs (menu, search, cart, filters, size guide, zoom) ---------- */
  var lastTrigger = null;

  theme.openDialog = function (dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('has-dialog');
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    var focusTarget = dialog.querySelector('[autofocus], input[type="search"]');
    if (focusTarget) focusTarget.focus();
  };

  theme.closeDialog = function (dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    var finish = function () {
      dialog.close();
      document.documentElement.classList.remove('has-dialog');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    };
    if (reduceMotion) finish(); else setTimeout(finish, 260);
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      theme.openDialog(document.getElementById(opener.getAttribute('data-open-dialog')), opener);
      return;
    }
    var closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      theme.closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop closes the dialog
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      theme.closeDialog(event.target);
    }
  });

  document.addEventListener('cancel', function (event) {
    if (event.target.tagName === 'DIALOG') {
      event.preventDefault();
      theme.closeDialog(event.target);
    }
  }, true);

  /* ---------- Sticky header ---------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var setHeaderHeight = function () {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight, { passive: true });
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------- Desktop dropdowns: open on hover, close on outside click / Escape ---------- */
  var canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
  document.querySelectorAll('[data-hover-details]').forEach(function (details) {
    details.addEventListener('mouseenter', function () { if (canHover.matches) details.open = true; });
    details.addEventListener('mouseleave', function () { if (canHover.matches) details.open = false; });
    details.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') { details.open = false; details.querySelector('summary').focus(); }
    });
  });
  document.addEventListener('click', function (event) {
    document.querySelectorAll('[data-hover-details][open], [data-quick-add][open]').forEach(function (details) {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  /* ---------- Footer accordions: collapsed on mobile, open on desktop ---------- */
  var footerMq = window.matchMedia('(min-width: 750px)');
  var syncFooter = function () {
    document.querySelectorAll('[data-footer-accordion]').forEach(function (d) { d.open = footerMq.matches; });
  };
  syncFooter();
  if (footerMq.addEventListener) footerMq.addEventListener('change', syncFooter);

  /* ---------- Announcement rotation ---------- */
  document.querySelectorAll('[data-announcement]').forEach(function (bar) {
    var messages = bar.querySelectorAll('.announcement__message');
    if (messages.length < 2 || reduceMotion) return;
    var index = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var paused = false;
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    bar.addEventListener('focusin', function () { paused = true; });
    bar.addEventListener('focusout', function () { paused = false; });
    setInterval(function () {
      if (paused) return;
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, interval);
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-qty-change]');
    if (!button) return;
    var wrapper = button.closest('[data-quantity]');
    var input = wrapper && wrapper.querySelector('input');
    if (!input) return;
    var step = parseInt(input.step, 10) || 1;
    var min = input.min !== '' ? parseInt(input.min, 10) : 0;
    var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
    var next = (parseInt(input.value, 10) || 0) + step * parseInt(button.getAttribute('data-qty-change'), 10);
    next = Math.max(min, Math.min(max, next));
    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Wishlist (stored in this browser) ---------- */
  var WISHLIST_KEY = 'arctic-elegance:wishlist';
  var wishlist = {
    read: function () {
      try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; }
    },
    write: function (list) {
      try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
    },
    has: function (handle) { return wishlist.read().indexOf(handle) !== -1; },
    toggle: function (handle) {
      var list = wishlist.read();
      var i = list.indexOf(handle);
      if (i === -1) list.unshift(handle); else list.splice(i, 1);
      wishlist.write(list.slice(0, 60));
      return i === -1;
    }
  };
  theme.wishlist = wishlist;

  theme.syncWishlistButtons = function (root) {
    var list = wishlist.read();
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var saved = list.indexOf(btn.getAttribute('data-wishlist-toggle')) !== -1;
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      btn.setAttribute('aria-label', saved ? btn.getAttribute('data-label-remove') : btn.getAttribute('data-label-add'));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  };

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    var handle = btn.getAttribute('data-wishlist-toggle');
    wishlist.toggle(handle);
    theme.syncWishlistButtons();
    var page = document.querySelector('[data-wishlist-page]');
    if (page && !wishlist.has(handle)) {
      var item = btn.closest('li');
      if (item && page.contains(item)) item.remove();
      renderWishlistEmpty(page);
    }
  });

  function renderWishlistEmpty(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    if (empty) empty.hidden = grid && grid.children.length > 0;
  }

  function renderWishlistPage() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    var grid = page.querySelector('[data-wishlist-grid]');
    var handles = wishlist.read();
    if (!handles.length) { renderWishlistEmpty(page); return; }
    var root = (theme.routes && theme.routes.root) || '/';
    if (root.slice(-1) !== '/') root += '/';
    Promise.all(handles.map(function (handle) {
      return fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=product-card-render')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (results) {
      var valid = [];
      results.forEach(function (html, i) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var card = doc.querySelector('[data-product-card]');
        if (!card) return;
        valid.push(handles[i]);
        var li = document.createElement('li');
        li.appendChild(document.importNode(card, true));
        grid.appendChild(li);
      });
      if (valid.length !== handles.length) wishlist.write(valid);
      theme.syncWishlistButtons();
      renderWishlistEmpty(page);
    });
  }

  var initWishlist = function () {
    theme.syncWishlistButtons();
    renderWishlistPage();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist, { once: true });
  } else {
    initWishlist();
  }

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', function (event) {
    theme.syncWishlistButtons(event.target);
  });

  window.theme = theme;
})();
